import { useEffect, useRef } from 'react';
import { workerRunLogHeading, type WorkerRunLog as RunLog } from '../lib/worker-run-log';

/** How close to the end the owner must be for new output to keep the end in view. */
const FOLLOW_WITHIN_PX = 48;

/**
 * One Worker's run log: what its process has printed, as Brunel recorded it. Read-only. It
 * has no input, sends nothing and opens nothing; the owner stops a run with Stop now.
 */
export function WorkerRunLog({ worker, log }: {
  readonly worker: { readonly number: number; readonly name: string };
  readonly log: RunLog | null;
}) {
  const box = useRef<HTMLPreElement>(null);
  const following = useRef(true);
  const text = log?.text ?? '';

  // New output keeps the end in view unless the owner has scrolled back to read.
  useEffect(() => {
    const node = box.current;
    if (node !== null && following.current) node.scrollTop = node.scrollHeight;
  }, [text]);

  const empty = log === null
    ? 'No run in progress. This Worker’s output appears here while it runs.'
    : 'Nothing recorded yet for this run. Output appears here as the Worker prints it.';

  return (
    <section className="worker-run-log" aria-label={`Run log for Worker ${worker.number}`} data-testid="worker-run-log"
      data-assignment-id={log?.assignmentId} data-run-state={log === null ? 'none' : log.running ? 'running' : 'ended'}
      data-run-department={log?.department ?? undefined}>
      <header className="row spread worker-run-log-head">
        <small className="worker-run-log-who">{workerRunLogHeading(worker, log)}</small>
        <small className="muted">
          Read-only{log !== null && log.observedAt !== null ? ` · updated ${new Date(log.observedAt).toLocaleTimeString()}` : ''}
        </small>
      </header>
      {text === ''
        ? <p className="muted worker-run-log-empty">{empty}</p>
        : (
          <pre ref={box} className="worker-run-log-text" role="log" aria-live="polite" tabIndex={0}
            aria-label={`Output of ${workerRunLogHeading(worker, log)}`}
            onScroll={(event) => {
              const node = event.currentTarget;
              following.current = node.scrollHeight - node.scrollTop - node.clientHeight <= FOLLOW_WITHIN_PX;
            }}>
            {text}
          </pre>
        )}
    </section>
  );
}
